import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "../components/AppIcon";
import { getStudentProgress } from "../api/client";

// StudentDetail — a single student's progress drill-down. Everything here comes
// from the tenant-scoped GET /api/institution/students/:id/progress endpoint,
// which surfaces the AI evaluation SUMMARY (scores, competency breakdown,
// strength/weakness titles, skill tags) but never the interview transcript —
// preserving the same privacy model as the rest of the console.

const TREND_META = {
  improving: { label: "Improving", icon: "TrendingUp", cls: "bg-success/15 text-success" },
  declining: { label: "Declining", icon: "TrendingDown", cls: "bg-destructive/10 text-destructive" },
  flat: { label: "Steady", icon: "Minus", cls: "bg-muted text-muted-foreground" },
  na: { label: "Not enough data", icon: "Minus", cls: "bg-muted text-muted-foreground" },
};

const STATUS_PILL = {
  assigned: "bg-primary/10 text-primary",
  started: "bg-warning/15 text-warning",
  completed: "bg-success/15 text-success",
  expired: "bg-muted text-muted-foreground",
  cancelled: "bg-muted text-muted-foreground",
};

const scoreText = (s) => (s == null ? "—" : Number(s).toFixed(1));
const scoreInt = (s) => (s == null ? "—" : String(Math.round(Number(s))));
const typeLabel = (v) => (v ? String(v).replace(/_/g, " ") : "—");

// Tone the score by the platform's pass band (≥60 pass; ≥75 strong).
function scoreTone(s) {
  if (s == null) return "text-muted-foreground";
  const n = Number(s);
  if (n >= 75) return "text-success";
  if (n >= 60) return "text-warning";
  return "text-destructive";
}

function barTone(s) {
  const n = Number(s) || 0;
  if (n >= 75) return "bg-success";
  if (n >= 60) return "bg-warning";
  return "bg-destructive";
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function StatusPill({ status }) {
  return (
    <span
      className={
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize " +
        (STATUS_PILL[status] || "bg-muted text-muted-foreground")
      }
    >
      {status || "assigned"}
    </span>
  );
}

function KpiCard({ icon, label, value, hint, tone }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon name={icon} size={16} />
        </span>
      </div>
      <div className={"mt-3 text-2xl font-semibold " + (tone || "text-foreground")}>{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

// Self-contained SVG line chart of overall score over completed interviews.
// Uses currentColor + theme text classes so strokes/labels pick up the console's
// CSS-variable palette (no chart lib, keeps the institution subtree dependency-free).
function TrendChart({ points }) {
  const W = 680;
  const H = 220;
  const padL = 30;
  const padR = 16;
  const padT = 16;
  const padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const n = points.length;

  const xAt = (i) => (n <= 1 ? padL + innerW / 2 : padL + (i / (n - 1)) * innerW);
  const yAt = (s) => padT + (1 - Math.max(0, Math.min(100, Number(s) || 0)) / 100) * innerH;
  const gridVals = [0, 25, 50, 75, 100];

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xAt(i).toFixed(1)} ${yAt(p.overallScore).toFixed(1)}`)
    .join(" ");

  // Only label every point when there are few; otherwise just the ends.
  const showEveryLabel = n <= 6;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-56 w-full" preserveAspectRatio="xMidYMid meet" role="img">
      {/* gridlines + y labels */}
      <g className="text-border">
        {gridVals.map((v) => (
          <line
            key={v}
            x1={padL}
            x2={W - padR}
            y1={yAt(v)}
            y2={yAt(v)}
            stroke="currentColor"
            strokeWidth="1"
            strokeDasharray={v === 0 ? "0" : "3 3"}
          />
        ))}
      </g>
      <g className="text-muted-foreground" fontSize="10">
        {gridVals.map((v) => (
          <text key={v} x={padL - 6} y={yAt(v) + 3} textAnchor="end" fill="currentColor">
            {v}
          </text>
        ))}
      </g>

      {/* line */}
      <g className="text-primary">
        <path d={linePath} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <circle key={i} cx={xAt(i)} cy={yAt(p.overallScore)} r="3.5" fill="currentColor" />
        ))}
      </g>

      {/* x labels */}
      <g className="text-muted-foreground" fontSize="10">
        {points.map((p, i) => {
          if (!showEveryLabel && i !== 0 && i !== n - 1) return null;
          const anchor = i === 0 ? "start" : i === n - 1 ? "end" : "middle";
          return (
            <text key={i} x={xAt(i)} y={H - 8} textAnchor={anchor} fill="currentColor">
              {fmtDate(p.date)}
            </text>
          );
        })}
      </g>
    </svg>
  );
}

function TagChips({ items, tone }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((t) => (
        <span
          key={t.tag}
          className={"inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium " + tone}
        >
          {t.tag}
          {t.count > 1 && <span className="opacity-70">×{t.count}</span>}
        </span>
      ))}
    </div>
  );
}

export default function StudentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getStudentProgress(id);
      setData(res);
    } catch (err) {
      setError(err?.error || "Could not load the student's progress.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const student = data?.student || null;
  const kpis = data?.kpis || null;
  const timeline = useMemo(() => (Array.isArray(data?.timeline) ? data.timeline : []), [data]);
  const competencies = useMemo(() => (Array.isArray(data?.competencies) ? data.competencies : []), [data]);
  const focusAreas = useMemo(() => (Array.isArray(data?.focusAreas) ? data.focusAreas : []), [data]);
  const strengthTags = useMemo(() => (Array.isArray(data?.strengthTags) ? data.strengthTags : []), [data]);
  const attempts = useMemo(() => (Array.isArray(data?.attempts) ? data.attempts : []), [data]);

  const trend = TREND_META[kpis?.trend] || TREND_META.na;
  const trendHint =
    kpis?.trend === "na"
      ? "Needs 2+ completed"
      : (kpis?.trendDelta > 0 ? "+" : "") + `${kpis?.trendDelta ?? 0} pts first → latest`;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* Back */}
      <button
        onClick={() => navigate("/institution/students")}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <Icon name="ArrowLeft" size={16} /> Students
      </button>

      {/* Load error */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <Icon name="AlertCircle" size={16} />
          <span>{error}</span>
          <button onClick={load} className="ml-auto font-medium underline">
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <div className="space-y-6">
          <div className="h-20 animate-pulse rounded-xl border border-border bg-muted/40" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-28 animate-pulse rounded-xl border border-border bg-muted/40" />
            ))}
          </div>
          <div className="h-64 animate-pulse rounded-xl border border-border bg-muted/40" />
        </div>
      ) : (
        !error &&
        student && (
          <>
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <h1 className="truncate text-2xl font-semibold text-foreground">{student.name}</h1>
                  <span className={"inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium " + trend.cls}>
                    <Icon name={trend.icon} size={12} />
                    {trend.label}
                  </span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {[student.email, student.batchName].filter(Boolean).join(" · ")}
                </p>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
                  {student.rollNumber && (
                    <span className="inline-flex items-center gap-1.5">
                      <Icon name="Hash" size={12} /> {student.rollNumber}
                    </span>
                  )}
                  {student.branch && (
                    <span className="inline-flex items-center gap-1.5">
                      <Icon name="BookOpen" size={12} /> {student.branch}
                    </span>
                  )}
                  {student.cgpa != null && (
                    <span className="inline-flex items-center gap-1.5">
                      <Icon name="GraduationCap" size={12} /> CGPA {student.cgpa}
                    </span>
                  )}
                  {student.graduationYear != null && (
                    <span className="inline-flex items-center gap-1.5">
                      <Icon name="Calendar" size={12} /> Class of {student.graduationYear}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <KpiCard
                icon="ClipboardList"
                label="Attempts"
                value={kpis?.totalAssigned ?? 0}
                hint={`${kpis?.completed ?? 0} completed · ${kpis?.inProgress ?? 0} in progress`}
              />
              <KpiCard
                icon="Gauge"
                label="Average score"
                value={kpis?.averageScore == null ? "—" : scoreText(kpis.averageScore)}
                tone={scoreTone(kpis?.averageScore)}
                hint="Across completed"
              />
              <KpiCard
                icon="Trophy"
                label="Best score"
                value={scoreInt(kpis?.bestScore)}
                tone={scoreTone(kpis?.bestScore)}
                hint={kpis?.latestScore != null ? `Latest ${scoreInt(kpis.latestScore)}` : "—"}
              />
              <KpiCard icon={trend.icon} label="Trend" value={trend.label} hint={trendHint} />
            </div>

            {/* Score over time */}
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold text-foreground">Score over time</h2>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    Overall score on each completed interview, oldest to newest.
                  </p>
                </div>
              </div>
              {timeline.length >= 2 ? (
                <div className="mt-4">
                  <TrendChart points={timeline} />
                </div>
              ) : (
                <div className="mt-4 flex h-40 items-center justify-center rounded-lg bg-muted/30 text-center text-sm text-muted-foreground">
                  {timeline.length === 1
                    ? "One interview completed so far — the trend line appears after the second."
                    : "No completed interviews yet."}
                </div>
              )}
            </div>

            {/* Competencies + focus areas */}
            {competencies.length > 0 && (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="rounded-xl border border-border bg-card p-5 lg:col-span-2">
                  <h2 className="text-base font-semibold text-foreground">Competency breakdown</h2>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    Average per-skill score across every evaluated interview.
                  </p>
                  <div className="mt-5 space-y-3">
                    {competencies.map((c) => (
                      <div key={c.name} className="flex items-center gap-3">
                        <span className="w-36 flex-shrink-0 truncate text-sm text-foreground" title={c.name}>
                          {c.name}
                        </span>
                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                          <div
                            className={"h-full rounded-full " + barTone(c.averageScore)}
                            style={{ width: `${Math.max(2, Math.min(100, c.averageScore))}%` }}
                          />
                        </div>
                        <span className={"w-10 flex-shrink-0 text-right text-sm font-medium " + scoreTone(c.averageScore)}>
                          {scoreInt(c.averageScore)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-card p-5">
                  <h2 className="text-base font-semibold text-foreground">Focus areas</h2>
                  <p className="mt-0.5 text-sm text-muted-foreground">Recurring weak spots to coach.</p>
                  <div className="mt-4">
                    {focusAreas.length > 0 ? (
                      <TagChips items={focusAreas} tone="bg-destructive/10 text-destructive" />
                    ) : (
                      <p className="text-sm text-muted-foreground">No recurring weak areas flagged yet.</p>
                    )}
                  </div>
                  {strengthTags.length > 0 && (
                    <>
                      <h3 className="mt-5 text-sm font-semibold text-foreground">Strengths</h3>
                      <div className="mt-3">
                        <TagChips items={strengthTags} tone="bg-success/15 text-success" />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Attempt history */}
            <div className="rounded-xl border border-border bg-card">
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <h2 className="text-base font-semibold text-foreground">Attempt history</h2>
                <span className="text-xs text-muted-foreground">
                  {attempts.length} attempt{attempts.length === 1 ? "" : "s"}
                </span>
              </div>

              {attempts.length === 0 ? (
                <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Icon name="ClipboardList" size={24} />
                  </span>
                  <h3 className="mt-4 text-base font-semibold text-foreground">No interviews assigned yet</h3>
                  <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                    Assign this student a campaign and their attempts and scores will appear here.
                  </p>
                  <button
                    onClick={() => navigate("/institution/campaigns")}
                    className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                  >
                    Go to campaigns <Icon name="ArrowRight" size={14} />
                  </button>
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {attempts.map((a) => (
                    <li key={a.id} className="px-5 py-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-foreground">{a.campaignName}</span>
                            <StatusPill status={a.status} />
                          </div>
                          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            <span className="inline-flex items-center gap-1.5 capitalize">
                              <Icon name="Layers" size={12} /> {typeLabel(a.interviewType)}
                            </span>
                            {a.difficulty && (
                              <span className="inline-flex items-center gap-1.5 capitalize">
                                <Icon name="BarChart2" size={12} /> {a.difficulty}
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1.5">
                              <Icon name="Calendar" size={12} />
                              {a.status === "completed"
                                ? `Completed ${fmtDateTime(a.completedAt)}`
                                : `Assigned ${fmtDate(a.assignedAt)}`}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-3">
                          {a.grade && (
                            <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-semibold text-foreground">
                              {a.grade}
                            </span>
                          )}
                          {a.status === "completed" && a.score != null ? (
                            <div className="text-right">
                              <span className={"text-lg font-semibold " + scoreTone(a.score)}>{scoreInt(a.score)}</span>
                              <span className="text-xs text-muted-foreground">/100</span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </div>
                      </div>

                      {/* Strengths / weaknesses snippet from the evaluation */}
                      {(a.strengths?.length > 0 || a.weaknesses?.length > 0) && (
                        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {a.strengths?.length > 0 && (
                            <div className="flex items-start gap-2 rounded-lg bg-success/5 px-3 py-2">
                              <Icon name="ThumbsUp" size={14} className="mt-0.5 flex-shrink-0 text-success" />
                              <span className="text-xs text-muted-foreground">
                                {a.strengths.slice(0, 3).join(" · ")}
                              </span>
                            </div>
                          )}
                          {a.weaknesses?.length > 0 && (
                            <div className="flex items-start gap-2 rounded-lg bg-destructive/5 px-3 py-2">
                              <Icon name="Target" size={14} className="mt-0.5 flex-shrink-0 text-destructive" />
                              <span className="text-xs text-muted-foreground">
                                {a.weaknesses.slice(0, 3).join(" · ")}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )
      )}
    </div>
  );
}
